import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Client } from '../../../context/ClientContext';
import { useUser } from '../../../context/UserContext';
import { Button, Card, StatCard, useToast } from '../../ui';
import {
  addJournalEntry,
  fetchAccountState,
  fetchAdsLink,
  fetchJournal,
  fetchKpis,
  fetchPeriodTotals,
  queueAdsCheckin,
  RANGES,
  fmtCount,
  fmtCustomerId,
  fmtMoney,
  setAdsAccount,
  setLeadActions,
  syncAdsNow,
  type AccountState,
  type AdsLink,
  type JournalEntry,
  type KpiRow,
  type PeriodTotals,
  type Range,
} from '../../../lib/googleAds';
import { formatRelativeDate } from '../../../utils/dateUtils';
import AdsAccountPicker from './googleAds/AdsAccountPicker';
import LeadActionsPicker from './googleAds/LeadActionsPicker';
import AdsKpiChart from './googleAds/AdsKpiChart';
import AccountStateCard from './googleAds/AccountStateCard';
import AdsJournal from './googleAds/AdsJournal';
import useMediaQuery, { PHONE_QUERY } from '../../clients/useMediaQuery';

interface GoogleAdsSectionProps {
  client: Client;
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

/**
 * Change against the same span just before: the text ("6% vs prior"), which way it moved, and whether that is
 * good. Nothing when either side has nothing to compare.
 */
function change(now: number | null | undefined, before: number | null | undefined, lowerIsBetter = false) {
  if (now == null || before == null || before === 0) return {};
  const pct = Math.round(((now - before) / before) * 100);
  if (pct === 0) return { delta: '0% vs prior', deltaTone: 'idle' as const, deltaDirection: 'up' as const };
  const rose = pct > 0;
  return {
    delta: `${Math.abs(pct)}% vs prior`,
    deltaDirection: rose ? ('up' as const) : ('down' as const),
    deltaTone: rose === lowerIsBetter ? ('down' as const) : ('up' as const),
  };
}

/**
 * Client detail → Google Ads: which account under the MCC is the client's and what counts as a lead there, the
 * trailing-30-day CPA logged every morning (scripts/lib/google-ads.mjs), the pinned account state, and the journal.
 */
export default function GoogleAdsSection({ client }: GoogleAdsSectionProps) {
  const toast = useToast();
  const { user } = useUser();
  const [link, setLink] = useState<AdsLink | null>(null);
  const [kpis, setKpis] = useState<KpiRow[]>([]);
  const [journal, setJournal] = useState<JournalEntry[]>([]);
  const [state, setState] = useState<AccountState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [queueing, setQueueing] = useState(false);
  // Sync now + Account Check-in beside the account number ran past the card's edge on a phone and broke the
  // number over three lines; there the buttons open the card body instead.
  const phone = useMediaQuery(PHONE_QUERY);

  const load = useCallback(async () => {
    try {
      const [l, k, j, s] = await Promise.all([fetchAdsLink(client.id), fetchKpis(client.id), fetchJournal(client.id), fetchAccountState(client.id)]);
      setLink(l);
      setKpis(k);
      setJournal(j);
      setState(s);
      setError(null);
    } catch (err) {
      console.error('Error loading Google Ads:', err);
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [client.id]);

  useEffect(() => {
    load();
  }, [load]);

  const sync = async (rebuild: boolean) => {
    setSyncing(true);
    try {
      await syncAdsNow(client.id, rebuild);
    } catch (err) {
      console.error('Error syncing Google Ads:', err);
      toast.error('Google Ads sync failed', err instanceof Error ? err.message : undefined);
    } finally {
      setSyncing(false);
      await load();
    }
  };

  const checkIn = async () => {
    setQueueing(true);
    try {
      const r = await queueAdsCheckin(client.id);
      if (r.skipped) toast.info('A check-in is already open', `Job ${r.job.id.slice(0, 8)} is ${r.job.status.replace(/_/g, ' ')}. It has to be submitted or cancelled first.`);
      else toast.success('Account check-in queued', 'It lands in the agent queue. The proposals come back for your review.');
    } catch (err) {
      console.error('Error queueing check-in:', err);
      toast.error('Couldn’t queue the check-in', err instanceof Error ? err.message : undefined);
    } finally {
      setQueueing(false);
    }
  };

  const pickAccount = async (customerId: string | null, label: string | null) => {
    try {
      await setAdsAccount(client.id, customerId);
      toast.success(customerId ? `Linked to ${label ?? fmtCustomerId(customerId)}` : 'Google Ads account unlinked', customerId ? 'Pulling its whole history…' : undefined);
      if (customerId) await sync(true);
      else await load();
    } catch (err) {
      console.error('Error saving Google Ads account:', err);
      toast.error('Couldn’t save the Google Ads account', err instanceof Error ? err.message : undefined);
    }
  };

  const saveLeads = async (ids: string[], before: string, after: string) => {
    try {
      await setLeadActions(client.id, ids);
      // The line means something else from here on; the journal says so, and the chart marks it.
      await addJournalEntry(client.id, {
        entry_date: new Date().toLocaleDateString('en-CA', { timeZone: 'America/Denver' }),
        kind: 'tracking_change',
        title: `Lead definition in Cockpit: ${after}`,
        body: `Was: ${before}. The CPA history was rebuilt on the new definition.`,
        review_by: null,
      });
      toast.success('Lead definition saved', 'Rebuilding the history…');
      await sync(true);
    } catch (err) {
      console.error('Error saving lead actions:', err);
      toast.error('Couldn’t save the lead definition', err instanceof Error ? err.message : undefined);
    }
  };

  // The chart's time frame drives the cards: totals for that span, live from Google, against the span before it.
  const [range, setRange] = useState<Range>('90');
  const [period, setPeriod] = useState<{ key: string; current: PeriodTotals; previous: PeriodTotals | null } | null>(null);
  const [periodError, setPeriodError] = useState<string | null>(null);
  const span = useMemo(() => {
    if (!kpis.length) return null;
    const to = kpis[kpis.length - 1].snapshot_date;
    if (range === 'all') return { from: kpis[0].snapshot_date, to, compare: false };
    const [y, m, d] = to.split('-').map(Number);
    return { from: new Date(Date.UTC(y, m - 1, d - Number(range) + 1)).toISOString().slice(0, 10), to, compare: true };
  }, [kpis, range]);
  const spanKey = span ? `${client.id}|${span.from}|${span.to}|${link?.leadActions.join(',')}` : null;

  useEffect(() => {
    if (!span || !spanKey) return;
    let live = true;
    setPeriodError(null);
    fetchPeriodTotals(client.id, span.from, span.to, span.compare)
      .then((r) => live && setPeriod({ key: spanKey, ...r }))
      .catch((err) => live && setPeriodError(err instanceof Error ? err.message : String(err)));
    return () => {
      live = false;
    };
  }, [client.id, span, spanKey]);

  const cur = period?.key === spanKey ? period.current : null;
  const prev = period?.key === spanKey ? period.previous : null;
  const frame = RANGES.find((r) => r.id === range)?.long ?? '';
  const pending = periodError ? 'n/a' : '…';

  if (error) {
    return (
      <Card eyebrow="Google Ads" title="Couldn’t load">
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--status-down)' }}>{error}</p>
      </Card>
    );
  }
  if (!link) {
    return (
      <Card eyebrow="Google Ads" title="Loading…">
        <div style={{ height: 80 }} />
      </Card>
    );
  }

  const headerButtons = link.customerId ? (
    <>
      <Button size="sm" variant="secondary" icon="refresh-cw" onClick={() => sync(false)} disabled={syncing}>
        {syncing ? 'Syncing…' : 'Sync now'}
      </Button>
      <Button size="sm" variant="primary" icon="list-checks" onClick={checkIn} disabled={queueing}>
        {queueing ? 'Queueing…' : 'Account Check-in'}
      </Button>
    </>
  ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <Card
        eyebrow="Google Ads"
        title={link.customerId ? fmtCustomerId(link.customerId) : 'No account linked'}
        actions={phone ? null : headerButtons}
      >
        {phone && headerButtons ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>{headerButtons}</div>
        ) : null}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-3)' }}>
          <AdsAccountPicker clientId={client.id} clientName={client.name} customerId={link.customerId} onPick={pickAccount} style={{ minWidth: 260 }} />
          {link.customerId ? <LeadActionsPicker customerId={link.customerId} selected={link.leadActions} onSave={saveLeads} /> : null}
        </div>
        {link.customerId ? (
          <p style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--text-2xs)', color: link.syncError ? 'var(--status-down)' : 'var(--text-faint)' }}>
            {link.syncError
              ? `Last sync failed ${formatRelativeDate(link.syncAt)}: ${link.syncError}`
              : link.syncAt
                ? `Synced ${formatRelativeDate(link.syncAt)}. Logged every morning at 7.`
                : 'Not synced yet.'}
          </p>
        ) : (
          <p style={{ margin: 'var(--space-3) 0 0', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
            Pick the client’s account under the Bullfinch MCC. The first sync fills the account's whole history.
          </p>
        )}
      </Card>

      {link.customerId ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-3)' }}>
            <StatCard
              label={`CPA · ${frame}`}
              value={cur ? fmtMoney(cur.cpa, 2) : pending}
              {...change(cur?.cpa, prev?.cpa, true)}
            />
            <StatCard label={`Leads · ${frame}`} value={cur ? fmtCount(cur.leads) : pending} {...change(cur?.leads, prev?.leads)} />
            <StatCard label={`Spend · ${frame}`} value={cur ? fmtMoney(cur.cost) : pending} {...change(cur?.cost, prev?.cost)} deltaTone="idle" />
            <StatCard
              label={`Clicks · ${frame}`}
              value={cur ? fmtCount(cur.clicks) : pending}
              hint={cur?.clicks ? `${fmtMoney(cur.cost / cur.clicks, 2)} CPC` : undefined}
            />
          </div>
          {periodError ? (
            <p style={{ margin: 0, fontSize: 'var(--text-2xs)', color: 'var(--status-down)' }}>Couldn’t load totals from Google Ads: {periodError}</p>
          ) : null}
          <AdsKpiChart rows={kpis} journal={journal} range={range} onRangeChange={setRange} />
        </>
      ) : null}

      <AccountStateCard clientId={client.id} state={state} userName={user?.name ?? null} onSaved={load} />
      <AdsJournal clientId={client.id} entries={journal} onChanged={load} />
    </div>
  );
}
